import { Analytics } from '@cobliteam/analytics';
import type { RouteDef } from '@cobliteam/dashboard/src/dashboardDef';
import { useLanguage } from '@cobliteam/i18n';
import { Divider, Tooltip } from '@cobliteam/ui/components';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { AnimatePresence, motion } from 'motion/react';
import { getAnimatedIcon } from './animatedIcons';
import LinkWrapper from './LinkWrapper';
import MostVisitedMenu from './MostVisitedMenu';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu as SidebarMenuBase,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarSecondaryLabel,
  SidebarTrigger,
  useSidebar,
} from './Sidebar';

interface SidebarMenuProps {
  primary?: boolean;
  primaryDef: RouteDef[];
  primaryHovered?: RouteDef;
  showTitle?: boolean;
  hoveredRoute?: RouteDef | null;
  onMouseEnter?: (route: RouteDef | null) => void;
  onMouseLeave?: () => void;
}

export function SidebarMenu({ primary, primaryDef, primaryHovered, hoveredRoute, onMouseEnter, onMouseLeave, showTitle = true }: SidebarMenuProps) {
  const { setOpenMobile, isLaptop } = useSidebar();
  const { currentLanguage } = useLanguage();

  const handleClick = (route: RouteDef) => {
    const title = getTextForLanguage(route.sidebar?.title, currentLanguage);
    if (primary) {
      Analytics.track('Sidebar - Primary - Navigate', { destination: title });
    } else {
      Analytics.track('Sidebar - Secondary - Navigate', { destination: title });
    }
  };

  if (primaryDef && !primaryHovered) {
    return primaryDef.map((route) => {
      const title = getTextForLanguage(route.sidebar?.title, currentLanguage);
      return (
        <LinkWrapper
          to={route.redirect ?? '#'}
          pageId={route.id}
          className='group/primary flex cursor-pointer flex-col items-center justify-center text-center text-gray-11'
          key={title}
          onMouseEnter={() => onMouseEnter?.(route)}
          onMouseLeave={() => onMouseLeave?.()}
          onClick={() => handleClick(route)}
        >
          <Tooltip content={title} asChild position='right' enable={!showTitle}>
            <div
              className={cn(
                'flex size-8 cursor-pointer items-center justify-center rounded-lg text-gray-11 duration-300 ease-in-out group-hover/primary:bg-primary-4 group-hover/primary:text-primary-12',
                route.sidebar?.active && 'bg-primary-12 text-white group-hover/primary:bg-primary-12 group-hover/primary:text-white'
              )}
            >
              {route.sidebar?.icon && getAnimatedIcon({ icon: route.sidebar?.icon as IconNames, isHovered: route.id === hoveredRoute?.id })}
            </div>
          </Tooltip>
          {showTitle && (
            <div className={cn('flex cursor-pointer flex-col items-center justify-center font-medium text-[11px] text-gray-11', route.sidebar?.active && 'text-primary-12')}>
              {title}
            </div>
          )}
        </LinkWrapper>
      );
    });
  }

  return (
    <AnimatePresence mode='wait'>
      {primaryHovered && (
        <SidebarGroup>
          <div className='mr-1 flex items-center justify-between'>
            <SidebarSecondaryLabel className='h-14'>{getTextForLanguage(primaryHovered?.sidebar?.title, currentLanguage)}</SidebarSecondaryLabel>
            {!isLaptop && <SidebarTrigger />}
          </div>
          <Divider className='mb-2 bg-gray-5' />
          <div className='space-y-0.5'>
            {primaryHovered?.routes?.map((item, index) => {
              const itemTitle = getTextForLanguage(item.sidebar?.title, currentLanguage);
              const itemIcon = item.sidebar?.icon || '';

              // component
              const component = item.component;
              if (component === 'most-visited-menu') return <MostVisitedMenu key={`${itemTitle}-${itemIcon}-${index}`} />;

              if (item.sidebar?.hide) return null;
              const hasGroup = item?.routes?.some((child) => !child.sidebar.hide);

              return (
                <motion.div
                  key={itemTitle + itemIcon}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{
                    type: 'tween',
                    damping: 25,
                    stiffness: 300,
                    duration: 0.4,
                  }}
                >
                  {hasGroup ? (
                    <SidebarGroup key={itemTitle}>
                      {index !== 0 && <Divider className='my-2 bg-gray-5' />}
                      <SidebarGroupLabel className='!text-xs !text-gray-12 font-medium'>{itemTitle}</SidebarGroupLabel>
                      <SidebarGroupContent>
                        <SidebarMenuSub className='translate-x-0 border-l-0 px-0'>
                          {item?.routes?.map((route) => {
                            if (route.sidebar?.hide) return null;
                            const goTo = route.redirect || route.url || '#';
                            const routeTitle = getTextForLanguage(route.sidebar?.title, currentLanguage);
                            const hasExternalLink = !!route.sidebar?.externalLink;

                            return (
                              <SidebarMenuItem key={routeTitle}>
                                <SidebarMenuButton asChild isActive={route.sidebar?.active} onClick={() => setOpenMobile(false)} title={routeTitle}>
                                  <LinkWrapper
                                    to={goTo}
                                    pageId={route.id}
                                    target={hasExternalLink ? '_blank' : '_self'}
                                    onClick={() => Analytics.track('Sidebar - Navigate', { destination: routeTitle, group: itemTitle })}
                                  >
                                    {route?.sidebar?.icon && <Icon name={route.sidebar?.icon as IconNames} className='size-4' />}
                                    <span className='flex-1'>{routeTitle}</span>
                                    {hasExternalLink && <Icon name='external-link' className='size-4' />}
                                  </LinkWrapper>
                                </SidebarMenuButton>
                              </SidebarMenuItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  ) : (
                    <SidebarGroup key={itemTitle}>
                      <SidebarMenuBase>
                        <SidebarMenuItem>
                          <SidebarMenuButton asChild isActive={item.sidebar?.active} onClick={() => setOpenMobile(false)}>
                            <LinkWrapper to={item.redirect || item.url || '#'} pageId={item.id} onClick={() => Analytics.track('Sidebar - Navigate', { destination: itemTitle })}>
                              {item?.sidebar?.icon && <Icon name={item.sidebar?.icon as IconNames} className='size-4' />}
                              <span className='flex-1'>{itemTitle}</span>
                            </LinkWrapper>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      </SidebarMenuBase>
                    </SidebarGroup>
                  )}
                </motion.div>
              );
            })}
          </div>
        </SidebarGroup>
      )}
    </AnimatePresence>
  );
}
